<template>
    <main class="main__profile__page__wrapper">
        <div class="rlms-container group__box">
            <aside class="profile__sidebar__box">
                <div class="user__profile__detail__box">
                    <div class="profile__avatar__box">
                        <img :src="userAvatar">
                    </div>
                    <div class="profile__user__info__box">
                        <h3 class="user__name__title">{{ authStore?.user?.name }}</h3>
                        <span class="user__email">{{ email }}</span>
                    </div>
                </div>
                <div class="profile__logout__box">
                    <a href="javascript:void(0)" class="purple__solid__btn" @click.prevent="logout">
                        <span class="btn__icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M13 8V5C13 3.89543 12.1046 3 11 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H11C12.1046 21 13 20.1046 13 19V16"
                                    stroke="#fff" stroke-width="1.5" stroke-linecap="round"></path>
                                <path
                                    d="M9 11.75C8.58579 11.75 8.25 12.0858 8.25 12.5C8.25 12.9142 8.58579 13.25 9 13.25L9 11.75ZM22.5303 13.0303C22.8232 12.7374 22.8232 12.2626 22.5303 11.9697L17.7574 7.1967C17.4645 6.90381 16.9896 6.90381 16.6967 7.1967C16.4038 7.48959 16.4038 7.96447 16.6967 8.25736L20.9393 12.5L16.6967 16.7426C16.4038 17.0355 16.4038 17.5104 16.6967 17.8033C16.9896 18.0962 17.4645 18.0962 17.7574 17.8033L22.5303 13.0303ZM9 13.25L22 13.25L22 11.75L9 11.75L9 13.25Z"
                                    fill="#fff"></path>
                            </svg>
                        </span>
                        <span class="btn__text">Logout</span>
                    </a>
                </div>
            </aside>

            <section class="profile__details__box__wrapper confirm__data__form__wrapper">
                <div class="profile__detail__title__box">
                    <h3 class="title">Change Password</h3>
                </div>
                <form @submit.prevent="onSubmit">
                    <div class="profile__information__wrapper join__form__wrapper">
                        <p class="rlms-mb-5">Please enter your current password, new password, and confirm the new password to update your password.</p>

                        <div class="form__group__field" style="display: block;">

                            <div class="input__field rlms-mb-3">
                                <label>Current Password</label>
                                <input type="password" placeholder="Current Password" v-model.trim="current_password" />
                                <span class="error__message" v-if="submitted && current_passwordError">
                                    {{ current_passwordError }}
                                </span>
                            </div>

                            <div class="input__field rlms-mb-3">
                                <label>New Password</label>
                                <input type="password" placeholder="New Password" v-model.trim="password" />
                                <span class="error__message" v-if="submitted && passwordError">
                                    {{ passwordError }}
                                </span>
                            </div>

                            <div class="input__field rlms-mb-3">
                                <label>Confirm New Password</label>
                                <input type="password" placeholder="Confirm New Password"
                                    v-model.trim="passwordConfirmation" />
                                <span class="error__message" v-if="submitted && passwordConfirmationError">
                                    {{ passwordConfirmationError }}
                                </span>
                            </div>

                        </div>

                    </div>

                    <div class="save__btn__box__wrapper" style="margin-top: 0;">
                        <button type="submit" class="save__changes__btn" :disabled="submitting">
                            Submit
                        </button>
                    </div>
                </form>
            </section>
        </div>
    </main>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';
import axios from '@/services/axios';
import { showToast } from '@/utils/swal';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';

const router = useRouter();
const authStore = useAuthStore();
const loader = useLoaderStore();

const submitting = ref(false);
const submitted = ref(false);

// change photo modal
const email = ref(null);
const userAvatar = ref(null);

// Replace this with props or API fetched user
const formSchema = yup.object({
    current_password: yup.string().required('Current password is required.'),
    password: yup.string()
        .required('New password is required.')
        .min(6, 'New password must be at least 6 characters.'),
    passwordConfirmation: yup.string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Please confirm the new password.'),
});

// Setup form with schema
const { handleSubmit, setErrors } = useForm({
    validationSchema: formSchema,
});

// Fields
const { value: current_password, errorMessage: current_passwordError } = useField('current_password');
const { value: password, errorMessage: passwordError } = useField('password');
const { value: passwordConfirmation, errorMessage: passwordConfirmationError } = useField('passwordConfirmation');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async () => {
        submitting.value = true;

        try {
            loader.show();
            const payload = {
                current_password: current_password.value,
                password: password.value,
                password_confirmation: passwordConfirmation.value,
            };

            const { data } = await axios.post('/user/change-password', payload);

            showToast({
                icon: 'success',
                title: 'Success',
                text: data.message || 'Password changed successfully.',
            });

            // Reset form after success
            current_password.value = '';
            password.value = '';
            passwordConfirmation.value = '';
            submitted.value = false;
        } catch (error) {
            if (error.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                setErrors(fieldErrors);
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

onMounted(async () => {
    fetchUser();
});

const logout = async () => {
    await authStore.logout();
    router.push('/');
}

const fetchUser = async () => {
    try {
        loader.show();

        await authStore.fetchUser();
        const user = authStore?.userinfo;

        email.value = user?.email;
        userAvatar.value = user?.profilePicture;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}
</script>